import '../style/global.css';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { IconButton, Popover, Stack, TextField, Typography } from '@mui/material';
import Box from "@mui/material/Box";
import { DataGrid } from '@mui/x-data-grid';
import { GridColDef } from '@mui/x-data-grid/models/colDef';
import React from "react";
import { useHistory, useLocation } from 'react-router-dom';
import { ScoreGrade, scoreColor } from '../Score';
import { ActivityEnum, AdminSubactivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { AudioApi } from '../api/AudioApi';
import { ISentenceScoreReportRow, ReportApi } from '../api/ReportApi';
import background from '../asset/background-login.png';
import SpeakerBlueIcon from '../asset/icon-speaker-blue.svg';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useKaiTiFontStyles } from '../font/KaiTiFont';

// npm install @mui/x-data-grid --legacy-peer-deps
// npm install @mui/x-data-grid@5.13.1

// What the DataGrid actually receives (id + Date conversion)
export type ReportGridRow =
    Omit<ISentenceScoreReportRow, 'sentence_score_datetime'> & {
        id: string;
        sentence_score_datetime: Date; // converted for DataGrid
    };

export default function SentenceScoreReport() {

    useUserActivityTracker(ActivityEnum.Admin, AdminSubactivityEnum.SentenceScoreReport)

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const [reportRowList, setReportRowList] = React.useState<ISentenceScoreReportRow[]>([]);
    const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 10 });

    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

    const [emailFilter, setEmailFilter] = React.useState('');

    const kaiTiFontClass = useKaiTiFontStyles();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedRowData, setSelectedRowData] = React.useState<ReportGridRow | null>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, row: ReportGridRow, index: number) => {
        setAnchorEl(event.currentTarget);
        setSelectedRowData(row);
        setSelectedIndex(index);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setSelectedRowData(null);
    };


    const columns: GridColDef<ReportGridRow>[] = [
        { field: 'sentence_score_id', headerName: 'Score ID', width: 90 },
        { field: 'sentence_id', headerName: 'Sentence ID', width: 90 },
        { field: 'user_id', headerName: 'User ID', width: 90 },
        { field: 'user_first_name', headerName: 'First Name', width: 150 },
        { field: 'user_last_name', headerName: 'Last Name', width: 150 },
        { field: 'user_email', headerName: 'Email', width: 200 },
        {
            field: 'text', headerName: 'Text', width: 180,
            renderCell: (params) => (
                <span
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                    {params.value}
                </span>
            ),
        },
        {
            field: 'score_mean', headerName: 'Score', type: 'number', width: 90,
            renderCell: (params) => (
                <span
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                    {params.value != null ? Number(params.value).toFixed(1) : ''}
                </span>
            ),
        },
        {
            field: 'pinyin_list_transcript', headerName: 'Correct Pinyin', width: 180,
            renderCell: (params) => {
                const row = params.row as ReportGridRow; // typed row
                return (
                    <span style={{ cursor: 'pointer', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }} >
                        {row.pinyin_list_transcript.join(" ")}
                    </span>
                )
            },
        },
        {
            field: 'pinyin_list_spoken', headerName: 'Spoken Pinyin', width: 180,
            renderCell: (params) => {
                const row = params.row as ReportGridRow; // typed row
                const isNoSpeech = !row.is_silence || row.is_silence.every(Boolean);
                const text = isNoSpeech ? "No Speech" : row.pinyin_list_spoken.join(" ")
                return (
                    <span style={{
                        cursor: 'pointer', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden',
                        color: isNoSpeech ? '#cccccc' : 'inherit', // grey when "No Speech"
                        fontStyle: isNoSpeech ? 'italic' : 'normal', // optional: italicize
                    }}>
                        {text}
                    </span>
                )
            },
        },
        {
            field: 'sentence_score_datetime',
            headerName: 'Timestamp',
            type: 'dateTime', // enables proper sorting and formatting
            width: 240,
            valueFormatter: (value: Date | string | null) =>
                value ? new Date(value).toLocaleString() : '',
        }

    ];

    React.useEffect(() => {
        ReportApi.getSentenceScore()
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to CourseApi.getAll() " + response.status)
            })
            .then(list => {
                setReportRowList(list);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [history, location, setErrorMessage]);



    const filteredRows: ReportGridRow[] = reportRowList
        .filter((row) =>
            emailFilter ? row.user_email.toLowerCase().includes(emailFilter.toLowerCase()) : true
        )
        .map((row) => ({
            ...row,
            id: `${row.user_id}-${row.sentence_score_id}`,
            sentence_score_datetime: new Date(row.sentence_score_datetime), // Convert to Date object
        }));

    const open = Boolean(anchorEl);


    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!open || selectedIndex == null) return;

            const { page: currentPage, pageSize } = paginationModel;
            const totalPages = Math.ceil(filteredRows.length / pageSize);
            let newPage = currentPage;
            let newIndex = selectedIndex;

            if (event.key === 'ArrowUp') {
                newIndex = Math.max(0, selectedIndex - 1);
            } else if (event.key === 'ArrowDown') {
                newIndex = Math.min(filteredRows.length - 1, selectedIndex + 1);
            } else if (event.key === 'ArrowLeft') {
                if (currentPage > 0) {
                    newPage = currentPage - 1;
                    const startIndex = newPage * pageSize;
                    const endIndex = Math.min(startIndex + pageSize, filteredRows.length);
                    newIndex = endIndex - 1; // last row of previous page
                } else {
                    return;
                }
            } else if (event.key === 'ArrowRight') {
                if (currentPage < totalPages - 1) {
                    newPage = currentPage + 1;
                    newIndex = newPage * pageSize; // first row of next page
                } else {
                    return;
                }
            } else {
                return;
            }

            const newRow = filteredRows[newIndex];
            setSelectedIndex(newIndex);
            setPaginationModel((prev) => ({ ...prev, page: newPage }));

            setTimeout(() => {
                const updatedRowEl = document.querySelector(`[data-id="${newRow.id}"]`);
                if (updatedRowEl) {
                    setSelectedRowData(newRow);
                    setAnchorEl(updatedRowEl as HTMLElement);
                    updatedRowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 50);

            event.preventDefault();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, selectedIndex, filteredRows, paginationModel]);

    const getAudio = (sentenceScoreId: number) => {
        AudioApi.getSentenceScoreAudio(sentenceScoreId)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to ScoreApi.getScoreBySentence() " + response.status)
            })
            .then(audioData => playAudio(audioData))
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    return (
        <div style={{
            display: 'flex', // Use flex to allow easy centering
            flexDirection: 'column', // Changed to stack children vertically
            justifyContent: 'center', // Center horizontally
            alignItems: 'center', // Center vertically
            // minHeight: 'calc(100vh - 85px)', // Ensure the div takes at least the full viewport height
            width: '100%', // Ensure the div takes the full viewport width
            padding: 0, // Reset any padding
            backgroundImage: `url(${background})`, // Your background image
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover', // Cover the entire size of the div
            backgroundPosition: 'center', // Center the background image
        }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '50%' }}>
                <TextField
                    label="Email"
                    value={emailFilter}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailFilter(e.target.value)}
                    fullWidth
                />
            </Box>

            <Box
                sx={{
                    mt: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    // maxHeight: 'calc(100vh - 300px)', // Viewport height minus 100px
                    width: '90%', // Set the width of the Box
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    overflowY: 'auto', // Allows content to scroll if it overflows the Y axis
                    border: '1.5px solid rgba(82, 99, 125, 0.2)', // Added border with color and transparency
                    padding: 4,
                    position: 'relative', // Required for absolute children to anchor
                }}
            >
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    autoHeight
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    onRowClick={(params, event) => {
                        const index = filteredRows.findIndex(row => row.id === params.id);
                        handlePopoverOpen(event, params.row, index)

                    }}

                    getRowClassName={(params) =>
                        selectedRowData != null && selectedRowData.id === params.row.id ? 'Mui-selected-row' : ''
                    }
                    sx={{
                        width: '100%',
                        '& .MuiDataGrid-row': {
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#F0F4FF', // light blue or any hover color
                        },
                        '& .MuiDataGrid-cell': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                        '& .Mui-selected-row': {
                            outline: '2px solid #0062FF',
                            backgroundColor: '#F0F4FF',
                        },
                    }}
                />
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 2, maxWidth: '80vw' }}>
                        {selectedRowData && (
                            <Stack spacing={2}>
                                {/* Speaker Icon and Close Icon in same row */}
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <IconButton onClick={() => getAudio(selectedRowData.sentence_score_id)}>
                                        <img src={SpeakerBlueIcon} style={{ width: '36px', height: '36px' }} />
                                    </IconButton>
                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <span style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem' }}>↑</span>
                                            <span style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem' }}>↓</span>
                                        </span>
                                        navigate rows
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <span style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem' }}>←</span>
                                            <span style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem' }}>→</span>
                                        </span>
                                        change page
                                    </Typography>

                                    <IconButton onClick={handlePopoverClose}>
                                        <CloseRoundedIcon />
                                    </IconButton>
                                </Stack>

                                {/* Pronunciation Breakdown */}
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                                    {selectedRowData.is_silence.every(x => x) ? (  // sentenceScoreAllSilence(selectedRowData) ? (
                                        <div>Sentence Incomplete</div>
                                    ) : (selectedRowData.pinyin_list_spoken.map((pinyinElement: string, index: number) => (
                                        <Stack direction="column">
                                            <Stack alignItems="center" justifyContent="center" key={index}
                                                sx={{
                                                    backgroundColor: `${scoreColor(selectedRowData!.score_grade[index])}22`,   // 22 is 13.33% opacity
                                                    borderTopLeftRadius: 4, borderTopRightRadius: 4,
                                                    px: 1
                                                }}>
                                                <Typography className={kaiTiFontClass.customFont} sx={{
                                                    fontSize: '24px'
                                                }}>
                                                    {selectedRowData!.text[index]}
                                                </Typography>
                                                <Typography sx={{
                                                    marginLeft: '0.5em', marginRight: '0.5em',
                                                }}>
                                                    {Math.round(selectedRowData!.score[index] * 100) / 100}
                                                </Typography>
                                                <Typography sx={{
                                                    marginLeft: '0.5em', marginRight: '0.5em',
                                                }}>
                                                    {pinyinElement}
                                                </Typography>
                                            </Stack>
                                            <Stack alignItems="center" justifyContent="center" key={index}
                                                sx={{
                                                    backgroundColor: `${scoreColor(ScoreGrade.Good)}22`,
                                                    borderBottomLeftRadius: 4, borderBottomRightRadius: 4,
                                                    px: 1
                                                }}>
                                                <Typography sx={{
                                                    marginLeft: '0.5em', marginRight: '0.5em',
                                                }}>
                                                    {selectedRowData!.pinyin_list_transcript[index]}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    )))}
                                </Stack>
                            </Stack>
                        )}
                    </Box>
                </Popover>
            </Box>
        </div>
    );
}